// Hierarchical step -> tank/lot source -> exam accordion for repetitive ANALYSIS
// operations, consuming the view-model built by analysisViewModel.js. Only the
// current step's rows are ever fed into an editable <Table> (unmodified, reused as-is);
// historical steps render plain read-only rows and future steps render an inert header
// only, after "Mostra tutti gli step" is pressed. Non-repetitive operations never reach
// this component (AnalysisDetail keeps its original flat <Table> render path for them).
import { memo, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import useLabels from "../../hooks/useLabels";
import { Icon } from "../Icon";
import Table from "../Table";
import RangeSlider from "../RangeSlider";

const sourceStateKey = (relativeId, source) => `${relativeId}:${source.sourceKind}:${source.sourceId}`;

// The view-model can rebuild `source` (and so `source.exams`) with a new object
// identity on every parent render even when the exams themselves haven't
// changed. A signature over what's actually shown/edited lets AnalysisExamRows
// keep the same `rows` array reference across such no-op renders, instead of
// handing Table a "new" defaultData it has to resync on every render.
const getExamRowsSignature = (exams = []) =>
  exams
    .map((exam) => {
      const row = exam?.rawReference ?? {};

      return [
        row?.relative_id ?? "",
        row?.tank_id ?? "",
        row?.batch_id ?? "",
        row?.substance_id ?? "",
        row?.value ?? "",
        row?.unit ?? "",
        row?.min_range ?? "",
        row?.max_range ?? "",
        row?.collection_date ?? "",
        row?.temperature ?? "",
        row?.analyst ?? "",
      ].join(":");
    })
    .join("|");

const domIds = (operationId, relativeId, source = null) => {
  const base = source
    ? `analysis-source-${operationId}-${relativeId}-${source.sourceKind}-${source.sourceId}`
    : `analysis-step-${operationId}-${relativeId}`;
  return { button: `${base}-button`, panel: `${base}-panel` };
};

const AnalysisExamRows = memo(function AnalysisExamRows({
  step,
  source,
  columns,
  errors,
  control,
  analysisState,
  getValues,
  setValue,
  isMobile,
}) {
  const [getLabel] = useLabels();
  const exams = source?.exams ?? [];
  const rowsSignature = getExamRowsSignature(exams);
  const rowsCacheRef = useRef({ signature: null, rows: [] });

  if (rowsCacheRef.current.signature !== rowsSignature) {
    rowsCacheRef.current = {
      signature: rowsSignature,
      rows: exams.map((exam) => exam.rawReference),
    };
  }

  const rows = rowsCacheRef.current.rows;

  if (step.isCurrent) {
    return (
      <Table
        defaultData={rows}
        columns={columns}
        errors={errors}
        control={control}
        isEditable={true}
        isSorting={false}
        isMobile={isMobile}
        isSearchBar={false}
        analysisState={analysisState}
        filterType={"analysis"}
        minEditableIndex={0}
        maxEditableIndex={rows.length}
        isLegend={false}
        newRow={{ quantity: "" }}
        setValue={setValue}
        getValues={getValues}
        type={"analysisDetail"}
      />
    );
  }

  return (
    <ul className="analysis_exam_rows">
      {exams.map((exam, index) => {
        const row = exam.rawReference;
        const hasValue = row?.value !== undefined && row?.value !== null && row?.value !== "";
        return (
          <li className="analysis_exam_row" key={exam?.key ?? `${row?.substance_id}-${index}`}>
            <div className="analysis_exam_row_main">
              <span className="analysis_exam_row_label">
                {getLabel(`substance_${row?.substance_name}`)}
              </span>
              <span className="analysis_exam_row_value">
                {hasValue
                  ? `${row.value}${row?.unit ? ` ${row.unit}` : ""}`
                  : getLabel("analysisNoResult")}
              </span>
              {exam?.hasRange && (
                <span
                  className={`analysis_exam_row_range_status ${
                    exam.isInside
                      ? "analysis_exam_row_range_status--ok"
                      : "analysis_exam_row_range_status--bad"
                  }`}
                  title={getLabel(exam.isInside ? "valueInsideRange" : "valueOutsideRange")}
                />
              )}
              {row?.analyst && <span className="analysis_exam_row_analyst">{row.analyst}</span>}
            </div>
            {hasValue && (
              <div className="analysis_exam_row_range">
                <RangeSlider
                  min={exam.min ?? undefined}
                  max={exam.max ?? undefined}
                  current={Number(row?.value)}
                  unit={row?.unit || ""}
                  hasRange={!!exam?.hasRange}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
});

const AnalysisSourceGroup = memo(function AnalysisSourceGroup({
  operationId,
  step,
  source,
  expanded,
  onToggle,
  columns,
  errors,
  control,
  analysisState,
  getValues,
  setValue,
  isMobile,
  configs,
  users,
}) {
  const [getLabel] = useLabels();
  const ids = domIds(operationId, step.relativeId, source);
  const sampledSummary = source.sampling.sampled
    ? `${getLabel("analysisSampled")} — ${
        source.sampling.sampledAt
          ? format(new Date(source.sampling.sampledAt), configs?.hourMediumDateFormat)
          : ""
      } (${users?.find((u) => Number(u.id) === Number(source.sampling.sampledByUserId))?.username || "-"})`
    : null;

  return (
    <div className="analysis_source_group">
      <button
        type="button"
        id={ids.button}
        aria-expanded={expanded}
        aria-controls={ids.panel}
        className="analysis_source_header"
        onClick={onToggle}
      >
        <Icon icon={expanded ? "fa-angle-up" : "fa-angle-down"} size="sm" aria-hidden="true" />
        <span className="analysis_source_identity">
          <span className="analysis_source_name">{source.sourceName}</span>
          {source.batchName && (
            <span className="analysis_source_batch">
              {source.batchName}
            </span>
          )}
        </span>
        {sampledSummary && <span className="analysis_source_sampling">{sampledSummary}</span>}
        {source.rangeStatus === "OUTSIDE" && (
          <span
            className="analysis_step_badge analysis_step_badge--outside"
            title={getLabel("valueOutsideRange")}
          >
            {getLabel("valueOutsideRange")}
          </span>
        )}
        {/* "Da campionare"/"To be sampled" is about *this source's* sampling state,
        not step editability in general -- showing it next to "Campionato" (sampled)
        would contradict it, so it's gated on source.sampling.sampled too. */}
        {!step.isCurrent && !source.sampling.sampled && (
          <span className="analysis_step_badge analysis_step_badge--readonly">
            {getLabel("analysisReadOnly")}
          </span>
        )}
      </button>
      {expanded && (
        <div id={ids.panel} role="region" aria-labelledby={ids.button} className="analysis_source_panel">
          <AnalysisExamRows
            step={step}
            source={source}
            columns={columns}
            errors={errors}
            control={control}
            analysisState={analysisState}
            getValues={getValues}
            setValue={setValue}
            isMobile={isMobile}
          />
        </div>
      )}
    </div>
  );
});

const AnalysisStepSection = memo(function AnalysisStepSection({
  operationId,
  step,
  totalSteps,
  expanded,
  onToggle,
  isSourceExpanded,
  onToggleSource,
  columns,
  errors,
  control,
  analysisState,
  getValues,
  setValue,
  isMobile,
  configs,
  users,
}) {
  const [getLabel] = useLabels();
  const ids = domIds(operationId, step.relativeId);

  return (
    <div className={`analysis_step_section analysis_step_section--${step.state}`}>
      <button
        type="button"
        id={ids.button}
        aria-expanded={expanded}
        aria-controls={ids.panel}
        className="analysis_step_header"
        onClick={onToggle}
      >
        <Icon icon={expanded ? "fa-angle-up" : "fa-angle-down"} size="sm" aria-hidden="true" />
        <span className="analysis_step_counter">{`${step.relativeId}/${totalSteps}`}</span>
        {step.isCurrent && (
          <span className="analysis_step_badge analysis_step_badge--current">
            {getLabel("analysisCurrentStep")}
          </span>
        )}
        {!step.isCurrent && step.hasResults && (
          <span className="analysis_step_badge">{getLabel("analysisResultsAvailable")}</span>
        )}
      </button>
      {expanded && (
        <div id={ids.panel} role="region" aria-labelledby={ids.button} className="analysis_step_panel">
          {step.sources.map((source) => (
            <AnalysisSourceGroup
              key={`${source.sourceKind}-${source.sourceId}`}
              operationId={operationId}
              step={step}
              source={source}
              expanded={isSourceExpanded(step, source)}
              onToggle={() => onToggleSource(step, source)}
              columns={columns}
              errors={errors}
              control={control}
              analysisState={analysisState}
              getValues={getValues}
              setValue={setValue}
              isMobile={isMobile}
              configs={configs}
              users={users}
            />
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Top-level accordion for a repetitive ANALYSIS operation. `viewModel` must come from
 * buildAnalysisTimelineViewModel(); only used when operation.repetitive is true.
 */
const AnalysisTimeline = ({
  operation,
  viewModel,
  columns,
  errors,
  control,
  analysisState,
  getValues,
  setValue,
  isMobile,
  configs,
  users,
}) => {
  const [getLabel] = useLabels();
  const [showAllSteps, setShowAllSteps] = useState(false);
  // Explicit user overrides only; the default (collapsed/expanded) always falls back
  // to the view-model's own defaultExpanded, so there is no "seed once" race against
  // data arriving after first mount (data starts empty and is enriched shortly after).
  const [stepOverrides, setStepOverrides] = useState({});
  const [sourceOverrides, setSourceOverrides] = useState({});
  const operationIdRef = useRef(operation?.id);

  useEffect(() => {
    if (operationIdRef.current === operation?.id) return;
    operationIdRef.current = operation?.id;
    setShowAllSteps(false);
    setStepOverrides({});
    setSourceOverrides({});
  }, [operation?.id]);

  const isStepExpanded = (step) => stepOverrides[step.relativeId] ?? step.defaultExpanded;
  const isSourceExpanded = (step, source) => {
    const key = sourceStateKey(step.relativeId, source);
    return sourceOverrides[key] ?? source.defaultExpanded;
  };
  const toggleStep = (step) =>
    setStepOverrides((prev) => ({ ...prev, [step.relativeId]: !isStepExpanded(step) }));
  const toggleSource = (step, source) => {
    const key = sourceStateKey(step.relativeId, source);
    setSourceOverrides((prev) => ({ ...prev, [key]: !isSourceExpanded(step, source) }));
  };

  const visibleSteps = viewModel.steps.filter(
    (step) => showAllSteps || step.relativeId <= viewModel.visibleThrough
  );

  return (
    <div className="analysis_timeline">
      {visibleSteps.map((step) =>
        step.isFuture ? (
          <div
            key={step.relativeId}
            className="analysis_step_section analysis_step_section--future"
          >
            <div className="analysis_step_header analysis_step_header--inert">
              <span className="analysis_step_counter">{`${step.relativeId}/${viewModel.totalSteps}`}</span>
            </div>
          </div>
        ) : (
          <AnalysisStepSection
            key={step.relativeId}
            operationId={operation?.id}
            step={step}
            totalSteps={viewModel.totalSteps}
            expanded={isStepExpanded(step)}
            onToggle={() => toggleStep(step)}
            isSourceExpanded={isSourceExpanded}
            onToggleSource={toggleSource}
            columns={columns}
            errors={errors}
            control={control}
            analysisState={analysisState}
            getValues={getValues}
            setValue={setValue}
            isMobile={isMobile}
            configs={configs}
            users={users}
          />
        )
      )}
      {viewModel.hiddenFutureCount > 0 && (
        <div className="analysis_timeline_footer">
          {!showAllSteps ? (
            <>
              <span>{getLabel("analysisFutureStepsHidden", { count: viewModel.hiddenFutureCount })}</span>
              <button type="button" onClick={() => setShowAllSteps(true)}>
                {getLabel("analysisShowAllSteps")}
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setShowAllSteps(false)}>
              {getLabel("analysisHideFutureSteps")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalysisTimeline;
